<template>
  <div class="pay-period-settings">
    <section class="settings-card">
      <div class="settings-card-header">
        <div class="settings-card-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M7 3v3M17 3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
            />
          </svg>
        </div>
        <div class="settings-card-heading">
          <h3>Pay Period</h3>
          <p>Set how your pay periods run — company-wide, or per project.</p>
        </div>
      </div>

      <div class="settings-form">
        <b-row class="settings-fields">
          <b-col cols="12" class="scope-col">
            <b-form-group label="Applies to" label-class="form-label-sm">
              <div class="scope-field">
                <b-form-select
                  v-model="selectedProjectId"
                  :options="projectSelectOptions"
                />
                <span
                  v-if="selectedProjectId !== 'all'"
                  class="scope-badge"
                  :class="isCustomContext ? 'is-override' : 'is-default'"
                >
                  {{ scopeBadgeLabel }}
                </span>
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Pay period length" label-class="form-label-sm">
              <b-form-select v-model="form.weeks" :options="weeksOptions" />
            </b-form-group>
          </b-col>
          <b-col v-if="form.weeks === 1" cols="12" md="6">
            <b-form-group label="Week starts on" label-class="form-label-sm">
              <b-form-select v-model="form.startDay" :options="dayOptions" />
            </b-form-group>
          </b-col>
          <b-col v-else cols="12" md="6">
            <b-form-group
              label="First day of the current pay period"
              label-class="form-label-sm"
            >
              <b-form-datepicker
                v-model="form.anchorDate"
                locale="en-AU"
                :date-format-options="dateFormatOptions"
                placeholder="Pick a date"
                today-button
                reset-button
                close-button
                show-decade-nav
                boundary="viewport"
                menu-class="anchor-datepicker-menu"
                class="anchor-datepicker"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <p class="scope-hint">{{ contextHint }}</p>

        <div class="settings-footer">
          <div v-if="currentPeriodLabel" class="current-period">
            <span class="current-period-label">Current period</span>
            <span class="current-period-value">{{ currentPeriodLabel }}</span>
          </div>
          <div class="footer-buttons">
            <b-button
              v-if="isCustomContext"
              type="button"
              variant="outline-secondary"
              class="reset-button"
              :disabled="saving"
              @click="resetToDefault"
            >
              Reset to default
            </b-button>
            <b-button
              type="button"
              variant="primary"
              class="save-button"
              :disabled="saving"
              @click="save"
            >
              <span v-if="saving" class="button-spinner" aria-hidden="true" />
              {{ saving ? 'Saving…' : saveLabel }}
            </b-button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { settingsStartDay, dayNameToIndex } from '@/utils/weekHelpers'

// "wednesday" -> "Wednesday". Used to show a project override's start day
// instead of a bare "custom" label, so the list reads at a glance.
function titleCaseDay(name) {
  if (!name) return ''
  var s = String(name)
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

export default {
  name: 'PayPeriodSettingsCard',
  data() {
    return {
      saving: false,
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      // 'all' = editing the company-wide schedule; a project id = editing that
      // project's override. The pay-period fields in `form` below always
      // reflect whichever scope is selected here.
      selectedProjectId: 'all',
      form: {
        weeks: 1,
        startDay: 1,
        anchorDate: '',
      },
      weeksOptions: Array.from({ length: 13 }, function (_, i) {
        var n = i + 1
        var text = n + ' week' + (n === 1 ? '' : 's')
        if (n === 1) text += ' (weekly)'
        if (n === 2) text += ' (fortnightly)'
        return { value: n, text: text }
      }),
      dayOptions: [
        { value: 0, text: 'Sunday' },
        { value: 1, text: 'Monday' },
        { value: 2, text: 'Tuesday' },
        { value: 3, text: 'Wednesday' },
        { value: 4, text: 'Thursday' },
        { value: 5, text: 'Friday' },
        { value: 6, text: 'Saturday' },
      ],
    }
  },
  computed: {
    settings() {
      return this.$store.getters['timesheet/timesheetSettings']
    },
    projectRows() {
      return this.$store.getters['timesheet/projectPayPeriods']
    },
    // "All projects" first, then every project for the "Applies to" dropdown.
    // Projects on their own schedule get a "— Custom" marker so the whole list
    // can be scanned at a glance (the badge beside the picker only flags the
    // one project that's currently selected).
    projectSelectOptions() {
      return [{ value: 'all', text: 'All projects (company default)' }].concat(
        (this.projectRows || []).map((r) => ({
          value: r.project_id,
          text: r.has_override ? r.project_name + ' — Custom' : r.project_name,
        })),
      )
    },
    // Badge next to the "Applies to" picker for the selected project: its
    // override start day (e.g. "Wednesday") rather than a bare "Custom".
    scopeBadgeLabel() {
      if (!this.isCustomContext) return 'Company default'
      return this.overrideSummary(this.selectedProjectRow)
    },
    selectedProjectRow() {
      if (this.selectedProjectId === 'all') return null
      return (
        (this.projectRows || []).find(
          (r) => r.project_id === this.selectedProjectId,
        ) || null
      )
    },
    isCustomContext() {
      return !!(this.selectedProjectRow && this.selectedProjectRow.has_override)
    },
    saveLabel() {
      return this.selectedProjectId === 'all' ? 'Save Pay Period' : 'Save'
    },
    // "fortnightly, starts Wednesday" — the company-wide schedule, in words.
    companyDefaultHint() {
      var s = this.settings
      if (!s) return 'weekly, starts Monday'
      var weeks = Number(s.pay_period_weeks) || 1
      var lengthLabel =
        weeks === 1 ? 'weekly' : weeks === 2 ? 'fortnightly' : weeks + ' weeks'
      var day = s.start_day
        ? s.start_day.charAt(0).toUpperCase() + s.start_day.slice(1)
        : 'Monday'
      return lengthLabel + ', starts ' + day
    },
    // The explainer under the fields — changes with the selected scope.
    contextHint() {
      if (this.selectedProjectId === 'all') {
        return 'Every project follows this schedule unless you give it its own — pick a project above to start.'
      }
      var name = this.selectedProjectRow
        ? this.selectedProjectRow.project_name
        : 'This project'
      if (this.isCustomContext) {
        return (
          '“' +
          name +
          '” has its own schedule. Reset to default puts it back on the All projects one (' +
          this.companyDefaultHint +
          ').'
        )
      }
      return (
        '“' +
        name +
        '” follows the All projects schedule (' +
        this.companyDefaultHint +
        '). Set a length and first day here, then Save, to give it its own — otherwise it keeps following All projects.'
      )
    },
    currentPeriodLabel() {
      var p =
        this.selectedProjectId === 'all'
          ? this.settings && this.settings.current_period
          : this.selectedProjectRow && this.selectedProjectRow.current_period
      if (!p) return ''
      return (
        dayjs(p.start_date).format('D MMM') +
        ' – ' +
        dayjs(p.end_date).format('D MMM')
      )
    },
  },
  watch: {
    settings: {
      immediate: true,
      handler(s) {
        this.syncFromSettings(s)
      },
    },
    selectedProjectId() {
      this.syncActiveForm()
    },
    // Re-seed the fields after a save/reset changes the selected project's row.
    projectRows() {
      if (this.selectedProjectId !== 'all') this.syncActiveForm()
    },
  },
  created() {
    this.$store.dispatch('timesheet/getProjectPayPeriods')
  },
  methods: {
    // Short, readable summary of a project's own schedule for the scope
    // badge — its start day ("starts Wednesday"), falling back to "custom"
    // only when the row has no day to show.
    overrideSummary(r) {
      var day = titleCaseDay(r && r.start_day)
      return day ? 'starts ' + day : 'custom'
    },
    // Seed form.weeks / startDay / anchorDate from the currently-selected
    // scope: the company settings for "All projects", the project's own
    // override if it has one, otherwise the company default (so a project
    // that inherits opens on the schedule it's actually following).
    syncActiveForm() {
      var row = this.selectedProjectRow
      var s = this.settings || {}
      var src =
        row && row.has_override
          ? {
              weeks: Number(row.pay_period_weeks) || 1,
              startDay: dayNameToIndex(row.start_day),
              anchorDate:
                (row.override && row.override.pay_period_anchor_date) ||
                row.pay_period_anchor_date ||
                '',
            }
          : {
              weeks: Number(s.pay_period_weeks) || 1,
              startDay: settingsStartDay(s),
              anchorDate: s.pay_period_anchor_date || '',
            }
      this.form.weeks = src.weeks || 1
      this.form.startDay = src.startDay == null ? 1 : src.startDay
      this.form.anchorDate = src.anchorDate
    },
    syncFromSettings(s) {
      if (!s) return
      // Company-wide pay-period fields only feed the form while "All projects"
      // is selected — otherwise they'd stomp a project override being edited.
      if (this.selectedProjectId === 'all') {
        this.form.weeks = Number(s.pay_period_weeks) || 1
        this.form.startDay = settingsStartDay(s)
        this.form.anchorDate =
          s.pay_period_anchor_date ||
          (s.current_period && s.current_period.start_date) ||
          ''
      }
    },
    // Most recent occurrence of the chosen weekday (today counts when today
    // is that weekday) — used as the anchor date for one-week periods.
    mostRecentWeekday(dayIndex) {
      var today = dayjs()
      var diff = (today.day() - dayIndex + 7) % 7
      return today.subtract(diff, 'day').format('YYYY-MM-DD')
    },
    async save() {
      var anchor
      if (this.form.weeks === 1) {
        anchor = this.mostRecentWeekday(this.form.startDay)
      } else {
        anchor = this.form.anchorDate
        if (!anchor) {
          this.$bvToast.toast(
            'Please pick the first day of the current pay period.',
            { title: 'Validation', variant: 'warning', solid: true },
          )
          return
        }
      }
      var forProject = this.selectedProjectId !== 'all'
      var name = this.selectedProjectRow
        ? this.selectedProjectRow.project_name
        : ''
      this.saving = true
      try {
        if (forProject) {
          await this.$store.dispatch('timesheet/saveProjectPayPeriod', {
            projectId: this.selectedProjectId,
            payPeriodWeeks: this.form.weeks,
            payPeriodAnchorDate: anchor,
          })
        } else {
          await this.$store.dispatch('timesheet/updateTimesheetSettings', {
            pay_period_weeks: this.form.weeks,
            pay_period_anchor_date: anchor,
          })
          // A company change shifts the resolved period for every inheriting
          // project too — refresh the per-project list so its rows stay right.
          this.$store.dispatch('timesheet/getProjectPayPeriods', {
            force: true,
          })
        }
        this.$bvToast.toast(
          forProject
            ? name + ' pay period saved.'
            : 'Pay period settings saved.',
          { title: 'Success', variant: 'success', solid: true },
        )
        // Let the surrounding dashboard re-resolve its pay-period window
        // without a full page reload. null = the company-wide schedule.
        this.$emit(
          'pay-period-saved',
          forProject ? this.selectedProjectId : null,
        )
      } catch (e) {
        this.$bvToast.toast(
          forProject
            ? 'Failed to save the project pay period.'
            : 'Failed to save pay period settings.',
          { title: 'Error', variant: 'danger', solid: true },
        )
      } finally {
        this.saving = false
      }
    },
    async resetToDefault() {
      if (this.selectedProjectId === 'all') return
      var name = this.selectedProjectRow
        ? this.selectedProjectRow.project_name
        : 'This project'
      this.saving = true
      try {
        await this.$store.dispatch(
          'timesheet/clearProjectPayPeriod',
          this.selectedProjectId,
        )
        this.$bvToast.toast(name + ' is back on the company default.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.$emit('pay-period-saved', this.selectedProjectId)
      } catch (e) {
        this.$bvToast.toast('Failed to reset the project pay period.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.pay-period-settings {
  margin-top: 20px;
}

.settings-card {
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  // Not `overflow: hidden` — that clipped the "first day of pay period"
  // date-picker calendar against the card edge on shorter screens. The
  // header/form below carry matching corner radii so the card still reads
  // as a single rounded panel.
  overflow: visible;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03),
    0 8px 24px rgba(15, 23, 42, 0.04);
}

.settings-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #eef2f6;
  background: #fff;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;

  .settings-card-icon {
    display: flex;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    color: #087ff5;
    background: #eef7ff;
    border-radius: 11px;

    svg {
      width: 21px;
      height: 21px;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  .settings-card-heading {
    min-width: 0;
  }

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.3;
    color: #172033;
  }

  p {
    margin: 5px 0 0;
    max-width: 610px;
    font-size: 13px;
    line-height: 1.5;
    color: #667085;
  }
}

.settings-form {
  padding: 22px 24px 24px;
  background: #fbfcfe;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
}

.settings-fields {
  margin-right: -8px;
  margin-left: -8px;

  > div {
    padding-right: 8px;
    padding-left: 8px;
  }
}

.settings-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding-top: 6px;
}

.current-period {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 36px;
  padding: 7px 11px;
  background: #f1f5f9;
  border: 1px solid #e5ebf2;
  border-radius: 9px;
}

.current-period-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.current-period-value {
  font-size: 13px;
  font-weight: 700;
  color: #243047;
}

.save-button {
  min-width: 148px;
  min-height: 40px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 700;
  border: 0;
  border-radius: 9px;
  box-shadow: 0 4px 10px rgba(0, 123, 255, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0, 123, 255, 0.24);
  }

  &:disabled {
    box-shadow: none;
  }
}

.button-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  margin-right: 6px;
  vertical-align: -2px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: settings-spin 0.7s linear infinite;
}

.scope-col /deep/ .form-group {
  margin-bottom: 6px;
}

/* b-form-datepicker's inner value <label> also carries class="form-control",
   so the `.settings-form .form-control` rule further down (white background,
   9px radius, fixed height) lands on it too and paints a second white box
   that escapes the wrapper's rounded border on the right. Both selectors here
   are deliberately more specific than that 3-class rule so the label stays
   transparent and shares the flex row, and the wrapper keeps the 50px height
   of its sibling selects. */
/deep/ .settings-form .anchor-datepicker.form-control {
  height: 50px;
}

/deep/ .settings-form .anchor-datepicker > label {
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
  height: auto;
  background-color: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

/* The calendar dropdown is a fixed-height panel; on a short window (or with
   the field low on the page) it can be taller than the space above or below.
   Two defences:
   1. Trim the calendar's own height (hide the keyboard-hint strip, shrink the
      day cells and inter-section gaps) so it fits above or below the field in
      almost every realistic scroll position — Popper then places it whole.
   2. boundary="viewport" + this max-height cap: a hard backstop for a genuinely
      tiny window — the popup scrolls inside itself instead of being clipped.
   Scoped to this datepicker via menu-class, so other pickers are untouched. */
/deep/ .anchor-datepicker-menu {
  max-height: calc(100vh - 16px);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  .b-calendar-grid-help {
    display: none;
  }

  .b-calendar-header,
  .b-calendar-nav {
    margin-bottom: 0.125rem;
  }

  .b-calendar-footer {
    margin-top: 0.25rem;
  }

  .b-calendar-grid-body .col[data-date] .btn {
    width: 28px;
    height: 28px;
    margin: 1px auto;
    padding: 7px 0;
    font-size: 13px;
  }
}

.scope-field {
  display: flex;
  align-items: center;
  gap: 12px;

  /deep/ .custom-select {
    flex: 1 1 auto;
    max-width: 460px;
    min-width: 0;
  }
}

.scope-badge {
  flex: 0 0 auto;
  align-self: center;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  padding: 5px 9px;
  border-radius: 999px;

  &.is-default {
    color: #64748b;
    background: #f1f5f9;
  }

  &.is-override {
    color: #0369a1;
    background: #e0f2fe;
  }
}

.scope-hint {
  margin: 0 0 18px;
  font-size: 12.5px;
  line-height: 1.55;
  color: #667085;
}

.footer-buttons {
  display: flex;
  gap: 8px;
}

.reset-button {
  min-height: 40px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 700;
  border-radius: 9px;
}

@keyframes settings-spin {
  to {
    transform: rotate(360deg);
  }
}

/deep/ .form-label-sm {
  margin-bottom: 7px;
  font-size: 12px;
  font-weight: 700;
  color: #475467;
  text-transform: none;
  letter-spacing: 0;
}

/deep/ .settings-form {
  .form-group {
    margin-bottom: 18px;
  }

  .form-control {
    height: 44px;
    font-size: 14px;
    color: #243047;
    background-color: #fff;
    border-color: #d7dee8;
    border-radius: 9px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.02);

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }
  }

  .custom-select {
    height: 44px;
    font-size: 14px;
    color: #243047;
    background-color: #fff;
    border-color: #d7dee8;
    border-radius: 9px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.02);

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }
  }
}

@media (max-width: 575.98px) {
  .settings-card-header {
    align-items: flex-start;
    padding: 18px;
  }

  .settings-form {
    padding: 18px;
  }

  .settings-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .current-period {
    justify-content: space-between;
    width: 100%;
  }

  .footer-buttons {
    width: 100%;
  }

  .footer-buttons .save-button,
  .footer-buttons .reset-button {
    flex: 1 1 0;
  }

  .save-button {
    width: 100%;
  }

  .scope-field {
    flex-wrap: wrap;
  }
}
</style>
